import { useDashboardAsesores, useDashboardEmpresas, useDashboardGlobal, usePendientesVerificacion } from '@/hooks';
import type { AsesorMetrics, EmpresaDashboard } from '@/types/types';
import { ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// ─── Helpers ─────────────────────────────────────────

function formatHours(h: number): string {
  const total = Math.round(h * 3600);
  const hrs = Math.floor(total / 3600);
  const min = Math.floor((total % 3600) / 60);
  if (hrs === 0) return `${min}m`;
  return `${hrs}h ${min}m`;
}

function ProgBar({
  value,
  color,
  fill,
  decimals = 0,
}: {
  value: number;
  color: 'green' | 'amber' | 'red';
  fill?: boolean;
  decimals?: number;
}) {
  const bg = { green: 'var(--admin-green)', amber: '#e09b00', red: 'var(--admin-red)' };
  return (
    <span className={`inline-flex items-center gap-1.5 ${fill ? 'w-full' : ''}`}>
      <span className={`admin-prog-bar bg-white ${fill ? 'flex-1' : ''}`}>
        <span className="admin-prog-fill" style={{ width: `${Math.min(value, 100)}%`, background: bg[color] }} />
      </span>
      <span className="text-[12px] font-medium">{value.toFixed(decimals)}%</span>
    </span>
  );
}

function progColor(v: number): 'green' | 'amber' | 'red' {
  if (v >= 70) return 'green';
  if (v >= 50) return 'amber';
  return 'red';
}

// ─── Metric Card ──────────────────────────────────────

function MetricCard({ label, value, sub, valueColor }: { label: string; value: string | number; sub: string; valueColor?: string }) {
  return (
    <div className="admin-metric-card">
      <div className="text-[11px] text-(--admin-text3) font-medium uppercase tracking-wider mb-1.5">{label}</div>
      <div className={`text-[26px] font-semibold tracking-tight leading-none ${valueColor ?? ''}`}>{value}</div>
      <div className="text-[11px] text-(--admin-text3) mt-1">{sub}</div>
    </div>
  );
}

// ─── Active Asesores Table ───────────────────────────

function AsesoresTable({ asesores }: { asesores: AsesorMetrics[] }) {
  const navigate = useNavigate();
  const top = [...asesores].sort((a, b) => b.totalEmpresas - a.totalEmpresas).slice(0, 5);

  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <div className="text-[13px] font-semibold text-(--admin-text)">Asesores activos</div>
        <div className="ml-auto">
          <button type="button" className="admin-btn text-[11px] py-1 px-2.5" onClick={() => navigate('/asesores')}>
            Ver todos →
          </button>
        </div>
      </div>
      <table className="admin-table">
        <thead>
          <tr>
            <th>Asesor</th>
            <th>Empresas</th>
            <th>Activas</th>
            <th>Cumpl.</th>
          </tr>
        </thead>
        <tbody>
          {top.map((a) => {
            return (
              <tr key={a.asesorId}>
                <td className="font-medium text-(--admin-text)">
                  <span
                    className="hover:text-(--admin-blue) hover:underline cursor-pointer transition-colors"
                    onClick={() => navigate(`/asesores/${a.asesorId}`)}
                  >
                    {a.nombre}
                  </span>
                </td>
                <td>{a.totalEmpresas}</td>
                <td className="font-medium">{a.asesoriasActivas}</td>
                <td>
                  <ProgBar value={a.cumplimientoPromedio} color={progColor(a.cumplimientoPromedio)} />
                </td>
              </tr>
            );
          })}
          {top.length === 0 && (
            <tr>
              <td colSpan={4} className="text-center text-(--admin-text3) py-8">
                Sin asesores registrados
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Companies Below Compliance ─────────────────────

function EmpresasBajoCumplimiento({ empresas, asesores }: { empresas: EmpresaDashboard[]; asesores: AsesorMetrics[] }) {
  const navigate = useNavigate();
  const bajo = [...empresas]
    .filter((e) => e.porcentajeCumplimiento < 60)
    .sort((a, b) => a.porcentajeCumplimiento - b.porcentajeCumplimiento)
    .slice(0, 5);

  const asesorMap = new Map(asesores.map((a) => [a.asesorId, a.nombre]));

  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <div className="text-[13px] font-semibold text-(--admin-text)">Empresas con bajo cumplimiento</div>
        <div className="ml-auto">
          <button type="button" className="admin-btn text-[11px] py-1 px-2.5" onClick={() => navigate('/empresas')}>
            Ver todas →
          </button>
        </div>
      </div>
      <table className="admin-table">
        <thead>
          <tr>
            <th>Empresa</th>
            <th>NIT</th>
            <th>Asesor</th>
            <th>Cumpl.</th>
          </tr>
        </thead>
        <tbody>
          {bajo.map((e) => (
            <tr key={e.nitId}>
              <td className="font-medium text-(--admin-text)">
                <span
                  className="hover:text-(--admin-blue) hover:underline cursor-pointer transition-colors"
                  onClick={() => navigate(`/empresas/${e.nitId}`)}
                >
                  {e.nombre}
                </span>
              </td>
              <td className="font-mono text-[11px] text-(--admin-text3)">{e.nitId}</td>
              <td className="text-(--admin-text2)">
                <span
                  className="hover:text-(--admin-blue) hover:underline cursor-pointer transition-colors"
                  onClick={() => navigate(`/asesores/${e.asesorId}`)}
                >
                  {asesorMap.get(e.asesorId) ?? '—'}
                </span>
              </td>
              <td>
                <ProgBar value={e.porcentajeCumplimiento} color={progColor(e.porcentajeCumplimiento)} />
              </td>
            </tr>
          ))}
          {bajo.length === 0 && (
            <tr>
              <td colSpan={4} className="text-center py-8">
                <span className="text-(--admin-green) font-medium">✓ Todas las empresas cumplen</span>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Loading Skeleton ──────────────────────────────

function MetricSkeleton() {
  return (
    <div className="admin-metric-card animate-pulse">
      <div className="h-3 w-20 bg-(--admin-surface2) rounded mb-3" />
      <div className="h-7 w-14 bg-(--admin-surface2) rounded mb-2" />
      <div className="h-2.5 w-24 bg-(--admin-surface2) rounded" />
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────

export function AdminDashboardPage() {
  const navigate = useNavigate();
  const { data: global, isLoading: loadingGlobal } = useDashboardGlobal();
  const { asesores, isLoading: loadingAsesores } = useDashboardAsesores();
  const { empresas, isLoading: loadingEmpresas } = useDashboardEmpresas();
  const { pendientes } = usePendientesVerificacion();

  const totalEmpresas = global?.totalEmpresas ?? 0;
  const metaHoras = totalEmpresas * 320;

  return (
    <>
      {/* KPI Metrics */}
      <div className="admin-metrics-row">
        {loadingGlobal ? (
          <>
            <MetricSkeleton />
            <MetricSkeleton />
            <MetricSkeleton />
            <MetricSkeleton />
          </>
        ) : (
          <>
            <MetricCard label="Empresas activas" value={totalEmpresas} sub={`${global?.totalAsesorias ?? 0} asesorías registradas`} />
            <MetricCard
              label="Cumplimiento General"
              value={`${(global?.cumplimientoPromedio ?? 0).toFixed(1)}%`}
              sub="Promedio de todas las empresas"
              valueColor="text-(--admin-green)"
            />
            <MetricCard
              label="Avance de Horas"
              value={`${(global?.horasAvancePorcentaje ?? 0).toFixed(1)}%`}
              sub={`Actual: ${formatHours(global?.horasTotales ?? 0)} - Meta: ${formatHours(metaHoras)} (320h × ${totalEmpresas} empresas)`}
              valueColor="text-(--admin-text)"
            />
            <MetricCard
              label="Asesorías en revisión"
              value={pendientes.length}
              sub="Requieren aprobación"
              valueColor={pendientes.length > 0 ? 'text-[var(--admin-amber)]' : ''}
            />
          </>
        )}
      </div>

      {/* Normas and Combinations Lists */}
      <div className="admin-two-col mb-6">
        {/* Cumplimiento por Norma */}
        <div className="admin-card mb-0">
          <div className="admin-card-header">
            <div className="text-[13px] font-semibold text-(--admin-text)">Cumplimiento por Norma</div>
            <button
              onClick={() => navigate('/normas')}
              className="ml-auto text-(--admin-blue) hover:underline text-[10px] font-bold flex items-center gap-1"
            >
              Ver todas <ExternalLink className="w-2.5 h-2.5" />
            </button>
          </div>
          <div className="p-4 flex flex-col gap-3">
            {loadingGlobal ? (
              <div className="animate-pulse flex flex-col gap-3">
                <div className="h-4 bg-(--admin-surface2) rounded w-3/4" />
                <div className="h-4 bg-(--admin-surface2) rounded w-5/6" />
                <div className="h-4 bg-(--admin-surface2) rounded w-2/3" />
                <div className="h-4 bg-(--admin-surface2) rounded w-1/2" />
              </div>
            ) : (
              <>
                {(global?.cumplimientoPorNorma ?? []).slice(0, 4).map((n) => (
                  <div key={n.ntcId} className="flex items-center justify-between gap-4">
                    <span
                      className="text-[12px] text-(--admin-text2) truncate flex-1 cursor-pointer hover:text-(--admin-blue) hover:underline font-medium"
                      title={n.nombre}
                      onClick={() => navigate(`/normas/${n.ntcId}`)}
                    >
                      {n.nombre}
                    </span>
                    <ProgBar value={n.porcentaje} color={progColor(n.porcentaje)} decimals={1} />
                  </div>
                ))}
                {(!global?.cumplimientoPorNorma || global.cumplimientoPorNorma.length === 0) && (
                  <div className="text-[12px] text-(--admin-text3) py-2">No hay datos de normas</div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Cumplimiento por Combinación */}
        <div className="admin-card mb-0">
          <div className="admin-card-header">
            <div className="text-[13px] font-semibold text-(--admin-text)">Cumplimiento por Combinación</div>
          </div>
          <div className="p-4 flex flex-col gap-3">
            {loadingGlobal ? (
              <div className="animate-pulse flex flex-col gap-3">
                <div className="h-4 bg-(--admin-surface2) rounded w-3/4" />
                <div className="h-4 bg-(--admin-surface2) rounded w-5/6" />
                <div className="h-4 bg-(--admin-surface2) rounded w-2/3" />
                <div className="h-4 bg-(--admin-surface2) rounded w-1/2" />
              </div>
            ) : (
              <>
                {(global?.cumplimientoPorCombinacion ?? []).slice(0, 4).map((c) => (
                  <div key={c.combinacionId} className="flex items-center justify-between gap-4">
                    <span className="text-[12px] text-(--admin-text2) truncate flex-1 font-medium" title={c.nombre}>
                      {c.nombre}
                      <span className="ml-2 text-[10px] text-(--admin-text3) font-normal">({c.totalEmpresas} empresas)</span>
                    </span>
                    <ProgBar value={c.porcentaje} color={progColor(c.porcentaje)} decimals={1} />
                  </div>
                ))}
                {(!global?.cumplimientoPorCombinacion || global.cumplimientoPorCombinacion.length === 0) && (
                  <div className="text-[12px] text-(--admin-text3) py-2 text-center">No hay combinaciones con empresas</div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Two column: Asesores + Empresas */}
      <div className="admin-two-col">
        {loadingAsesores ? <div className="admin-card h-48 animate-pulse" /> : <AsesoresTable asesores={asesores} />}

        {loadingEmpresas ? (
          <div className="admin-card h-48 animate-pulse" />
        ) : (
          <EmpresasBajoCumplimiento empresas={empresas} asesores={asesores} />
        )}
      </div>
    </>
  );
}
